import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCreateSessionType,
  useDeleteSessionType,
  useSessionTypes,
  useUpdateSessionType,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal, ConfirmDialog } from '@/components/ui/Modal'
import { TextField, TextAreaField } from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatDuration } from '@/lib/format'
import type { SessionType } from '@/types/api'
import styles from './Masters.module.css'

/**
 * A4 — Session type master.
 *
 * *** DURATION DRIVES THE CALENDAR AND THE PRICE ***
 * A student picks a session type BEFORE choosing a slot, and its duration
 * decides how the mentor's day is divided. Changing a duration therefore
 * changes every future slot grid — which is why the edit form says so.
 */
export function SessionTypesPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useSessionTypes()

  const createMutation = useCreateSessionType()
  const updateMutation = useUpdateSessionType()
  const deleteMutation = useDeleteSessionType()

  const [editing, setEditing] = useState<SessionType | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<SessionType | null>(null)

  const sessionTypes = data?.session_types ?? []
  const activeCount = sessionTypes.filter((type) => type.is_active).length

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const input = {
      name: String(form.get('name') ?? '').trim(),
      description: String(form.get('description') ?? '').trim() || null,
      duration_minutes: Number(form.get('duration_minutes') ?? 30),
      display_order: Number(form.get('display_order') ?? 0),
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.session_type_id, input })
        toast.success(`“${input.name}” updated.`)
        setEditing(null)
      } else {
        await createMutation.mutateAsync(input)
        toast.success(`“${input.name}” added.`)
        setCreating(false)
      }
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save it.',
      )
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.session_type_id)
      toast.success(`“${deleting.name}” removed.`)
      setDeleting(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove it.',
      )
      setDeleting(null)
    }
  }

  const columns: Array<Column<SessionType>> = [
    {
      key: 'name',
      header: 'Session type',
      render: (row) => (
        <div>
          <span className={styles.primaryText}>{row.name}</span>
          {row.description && (
            <p className={styles.secondaryText}>{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      render: (row) => (
        <span className="numeric">{formatDuration(row.duration_minutes)}</span>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      align: 'right',
      secondary: true,
      render: (row) => <span className="numeric">{row.display_order}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.is_active ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">Hidden</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '150px',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(row)}>
            Remove
          </Button>
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Session types"
        description="What a student books. The duration decides both the slot length and which pricing rule applies."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add session type
          </Button>
        }
      />

      {!isLoading && activeCount === 0 && (
        <Callout tone="danger" title="No active session types">
          <p>
            Students cannot book anything until at least one session type is
            active — there is nothing for them to choose.
          </p>
        </Callout>
      )}

      <Card flush>
        <Table
          columns={columns}
          rows={sessionTypes}
          rowKey={(row) => row.session_type_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No session types yet. Add one — for example “Quick chat, 15 minutes”."
        />
      </Card>

      <Modal
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        title={editing ? 'Edit session type' : 'Add session type'}
      >
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          key={editing?.session_type_id ?? 'new'}
        >
          <TextField
            label="Name"
            name="name"
            defaultValue={editing?.name ?? ''}
            placeholder="e.g. Standard session"
            required
            autoFocus
            maxLength={100}
          />

          <TextField
            label="Duration"
            name="duration_minutes"
            type="number"
            min={5}
            max={480}
            step={5}
            defaultValue={String(editing?.duration_minutes ?? 30)}
            required
            hint={
              editing
                ? 'Changing this affects future bookings only. Sessions already booked keep their original length.'
                : 'In minutes. This divides the mentor’s available hours into slots.'
            }
          />

          <TextAreaField
            label="Description"
            name="description"
            defaultValue={editing?.description ?? ''}
            placeholder="Helps a student choose between types."
            maxLength={500}
          />

          <TextField
            label="Display order"
            name="display_order"
            type="number"
            min={0}
            defaultValue={String(editing?.display_order ?? 0)}
            hint="Lower numbers appear first."
          />

          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setCreating(false)
                setEditing(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save changes' : 'Add session type'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Remove this session type?"
        message={
          <>
            Students will no longer be able to book{' '}
            <strong>{deleting?.name}</strong>. Existing bookings are not
            affected — they keep the duration and price agreed at the time.
          </>
        }
        confirmLabel="Remove"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageStack>
  )
}
